import { ChevronDown, ChevronUp, Clock, ExternalLink } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "../../../../shared/lib/utils.js";
import {
  formatNotificationCreatedAt,
  isNotificationUnread,
} from "../lib/format-notification-display.js";
import { resolveNotificationCategoryLabel } from "../lib/resolve-notification-category-label.js";
import { toSpaSaleNotificationPath } from "../lib/to-spa-sale-notification-path.js";
import { NotificationReopenActions } from "./notification-reopen-actions.js";

/**
 * @param {Object} props
 * @param {string} props.label
 * @param {boolean} props.isUnread
 * @param {boolean} [props.expanded]
 * @param {() => void} [props.onToggle]
 * @param {boolean} props.disabled
 * @param {boolean} [props.readOnly]
 * @param {boolean} [props.canExpand]
 */
function NotificationCardHeader({
  label,
  isUnread,
  expanded = false,
  onToggle,
  disabled,
  readOnly = false,
  canExpand = false,
}) {
  const ChevronIcon = expanded ? ChevronUp : ChevronDown;

  return (
    <div className="flex items-center justify-between gap-2">
      <span
        className={cn(
          "inline-flex items-center rounded-full px-2.5 py-0.5",
          "text-[11px] font-semibold tracking-tight",
          readOnly
            ? "bg-muted text-muted-foreground"
            : "bg-primary text-white",
        )}
      >
        {label}
      </span>
      <div className="flex items-center gap-2">
        {isUnread && !readOnly ? (
          <span
            className="h-2 w-2 shrink-0 rounded-full bg-primary"
            aria-hidden
          />
        ) : (
          <span className="h-2 w-2 shrink-0" aria-hidden />
        )}
        {canExpand ? (
          <button
            type="button"
            disabled={disabled}
            aria-expanded={expanded}
            aria-label={
              expanded ? "Recolher notificação" : "Expandir notificação"
            }
            onClick={(event) => {
              event.stopPropagation();
              onToggle?.();
            }}
            className={cn(
              "rounded-md p-0.5 text-muted-foreground transition-colors",
              !readOnly && "hover:text-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              disabled && "pointer-events-none opacity-60",
            )}
          >
            <ChevronIcon className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * @param {Object} props
 * @param {string} props.label
 */
function NotificationCardTimestamp({ label }) {
  if (!label) return null;
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>{label}</span>
    </div>
  );
}

/**
 * Card de notificação.
 * - Com `requiresAction`: expansível (chevron + ações ao expandir).
 * - Sem ação: conteúdo sempre visível, sem expansão; Abrir marca/navega.
 * - `readOnly` (aba Lidas): histórico muted, sem Abrir/ações.
 *
 * @param {Object} props
 * @param {Object} props.notification
 * @param {(id: string) => void} [props.onOpen]
 * @param {(id: string) => void} [props.onMarkRead]
 * @param {boolean} [props.readOnly]
 * @param {boolean} [props.isMarking]
 * @param {boolean} [props.showReopenActions]
 * @param {boolean} [props.canSubmitReopen]
 * @param {boolean} [props.isReopenBusy]
 * @param {() => void|Promise<void>} [props.onApproveReopen]
 * @param {(reason?: string) => void|Promise<void>} [props.onRejectReopen]
 */
export function NotificationCard({
  notification,
  onOpen,
  onMarkRead,
  readOnly = false,
  isMarking = false,
  showReopenActions = false,
  canSubmitReopen = false,
  isReopenBusy = false,
  onApproveReopen,
  onRejectReopen,
}) {
  const [expanded, setExpanded] = useState(false);
  const isUnread = isNotificationUnread(notification);
  const createdLabel = formatNotificationCreatedAt(notification?.createdAt);
  const categoryLabel =
    notification?.categoryLabel ||
    resolveNotificationCategoryLabel({
      module: notification?.module,
      type: notification?.type,
    });
  const title = notification?.title || "Notificação";
  const body =
    typeof notification?.body === "string" ? notification.body.trim() : "";
  const disabled = isMarking || isReopenBusy;
  const canExpand =
    !readOnly && Boolean(notification?.requiresAction);
  const spaPath = toSpaSaleNotificationPath(notification?.deeplink, {
    notificationType: notification?.type || null,
    requesterUserId: notification?.requesterUserId ?? null,
  });
  const showDetails = !canExpand || expanded;
  const showOpenLink = !readOnly && showDetails && Boolean(spaPath);
  const showActions = !readOnly && canExpand && expanded && showReopenActions;
  const isPlainClickable = !readOnly && !canExpand;

  const toggleExpanded = () => {
    if (disabled || !canExpand) return;
    setExpanded((prev) => !prev);
  };

  const handleBodyClick = () => {
    if (disabled || readOnly || canExpand) return;
    if (isUnread && notification?.id && onMarkRead) {
      onMarkRead(notification.id);
    }
  };

  return (
    <div
      className={cn(
        "w-full rounded-lg border p-3 text-left transition-colors",
        readOnly
          ? "border-border/60 bg-muted/30 text-muted-foreground"
          : isUnread
            ? "border-primary/30 bg-primary/5"
            : "border-border bg-background",
        isPlainClickable &&
          "cursor-pointer hover:border-primary/40 hover:bg-muted/40",
        isPlainClickable && isUnread && "hover:bg-primary/10",
        disabled && "opacity-60",
      )}
    >
      <NotificationCardHeader
        label={categoryLabel}
        isUnread={isUnread}
        expanded={expanded}
        onToggle={toggleExpanded}
        disabled={disabled}
        readOnly={readOnly}
        canExpand={canExpand}
      />

      {canExpand ? (
        <button
          type="button"
          disabled={disabled}
          onClick={toggleExpanded}
          className={cn(
            "mt-2 w-full space-y-2 text-left",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          <p className="text-sm font-semibold text-foreground">{title}</p>
          {showDetails ? (
            <>
              <NotificationCardTimestamp label={createdLabel} />
              {body ? (
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                  {body}
                </p>
              ) : null}
            </>
          ) : (
            <>
              {body ? (
                <p className="line-clamp-2 text-sm text-muted-foreground">
                  {body}
                </p>
              ) : null}
              <NotificationCardTimestamp label={createdLabel} />
            </>
          )}
        </button>
      ) : (
        <div
          role={readOnly ? undefined : "button"}
          tabIndex={readOnly || disabled ? undefined : 0}
          onClick={handleBodyClick}
          onKeyDown={(event) => {
            if (readOnly || disabled) return;
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleBodyClick();
            }
          }}
          className={cn(
            "mt-2 w-full space-y-2 text-left",
            !readOnly &&
              "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            readOnly && "cursor-default",
          )}
        >
          <p
            className={cn(
              "text-sm font-semibold",
              readOnly ? "text-muted-foreground" : "text-foreground",
            )}
          >
            {title}
          </p>
          <NotificationCardTimestamp label={createdLabel} />
          {body ? (
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">
              {body}
            </p>
          ) : null}
        </div>
      )}

      {showOpenLink ? (
        <div className="mt-3">
          <Link
            to={spaPath}
            aria-disabled={disabled || undefined}
            tabIndex={disabled ? -1 : undefined}
            className={cn(
              "inline-flex items-center text-sm font-medium text-primary",
              "underline-offset-4 hover:underline",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              disabled && "pointer-events-none opacity-60",
            )}
            onClick={(event) => {
              event.stopPropagation();
              if (disabled) {
                event.preventDefault();
                return;
              }
              if (notification?.id) onOpen?.(notification.id);
            }}
          >
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            Abrir
          </Link>
        </div>
      ) : null}

      {showActions ? (
        <NotificationReopenActions
          onApprove={onApproveReopen}
          onReject={onRejectReopen}
          disabled={isMarking}
          isBusy={isReopenBusy}
          canSubmit={canSubmitReopen}
        />
      ) : null}
    </div>
  );
}
